import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  Button,
  Breadcrumbs,
  Link,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Search as SearchIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  CloudDownload as CloudDownloadIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const DocumentationPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const categories = [
    { title: 'Getting Started', icon: <DescriptionIcon /> },
    { title: 'API Reference', icon: <CodeIcon /> },
    { title: 'Configuration', icon: <SettingsIcon /> },
    { title: 'Integration', icon: <CloudDownloadIcon /> },
    { title: 'Security', icon: <SecurityIcon /> }
  ];

  const articles = [
    {
      category: 'Getting Started',
      title: 'Introduction to Contract AI',
      description: 'Learn the basics of Contract AI and how it can help your business.',
      url: '#introduction'
    },
    {
      category: 'Getting Started',
      title: 'Quick Start Guide',
      description: 'Get up and running with Contract AI in just a few minutes.',
      url: '#quick-start'
    },
    {
      category: 'Getting Started',
      title: 'Creating Your First Document',
      description: 'Step-by-step guide to creating your first document with Contract AI.',
      url: '#first-document'
    },
    {
      category: 'API Reference',
      title: 'Authentication',
      description: 'Learn how to authenticate with the Contract AI API.',
      url: '#api-auth'
    },
    {
      category: 'API Reference',
      title: 'Documents API',
      description: 'Detailed documentation for the Documents API endpoints.',
      url: '#documents-api'
    },
    {
      category: 'API Reference',
      title: 'Templates API',
      description: 'Detailed documentation for the Templates API endpoints.',
      url: '#templates-api'
    },
    {
      category: 'Configuration',
      title: 'User Settings',
      description: 'Configure your user settings and preferences.',
      url: '#user-settings'
    },
    {
      category: 'Configuration',
      title: 'Team Configuration',
      description: 'Set up and manage your team in Contract AI.',
      url: '#team-config'
    },
    {
      category: 'Integration',
      title: 'CRM Integration',
      description: 'Integrate Contract AI with your CRM system.',
      url: '#crm-integration'
    },
    {
      category: 'Integration',
      title: 'Document Management Systems',
      description: 'Connect Contract AI to your document management system.',
      url: '#dms-integration'
    },
    {
      category: 'Security',
      title: 'Data Security',
      description: 'Learn about how Contract AI secures your data.',
      url: '#data-security'
    },
    {
      category: 'Security',
      title: 'Compliance',
      description: 'Information about Contract AI compliance with regulations.',
      url: '#compliance'
    }
  ];

  const filteredArticles = searchQuery 
    ? articles.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        article.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : articles.filter(article => article.category === categories[selectedTab].title);

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 6, pt: 4 }}>
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Documentation
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
            Everything you need to know about using Contract AI effectively
          </Typography>
          
          <Box sx={{ maxWidth: '600px', mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search documentation..."
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'white' }} />
                  </InputAdornment>
                ),
                sx: { 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  color: 'white'
                }
              }}
            />
          </Box>
          
          <Breadcrumbs aria-label="breadcrumb" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <Link color="inherit" href="/">
              Home
            </Link>
            <Typography color="rgba(255,255,255,0.9)">Documentation</Typography>
          </Breadcrumbs>
        </Container>
      </Box>
      
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Documentation
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedTab}
                onChange={handleTabChange}
                sx={{ borderRight: 1, borderColor: 'divider' }}
              >
                {categories.map((category, index) => (
                  <Tab 
                    key={index} 
                    label={category.title} 
                    icon={category.icon} 
                    iconPosition="start"
                    sx={{ 
                      alignItems: 'flex-start', 
                      textAlign: 'left',
                      minHeight: '48px'
                    }} 
                  />
                ))}
              </Tabs>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Need Help?
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                fullWidth
                sx={{ mt: 1 }}
              >
                Contact Support
              </Button>
            </Paper>
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              {searchQuery ? 'Search Results' : categories[selectedTab].title}
            </Typography>
            
            {searchQuery && filteredArticles.length === 0 ? (
              <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No results found for "{searchQuery}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try different keywords or browse the categories
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {filteredArticles.map((article, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper 
                      sx={{ 
                        p: 3, 
                        borderRadius: 2, 
                        height: '100%',
                        transition: '0.3s',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        <Link href={article.url} color="inherit" underline="hover">
                          {article.title}
                        </Link>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {article.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {article.category}
                        </Typography>
                        <Button 
                          variant="text" 
                          size="small"
                          href={article.url}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>
      
      {/* Help Section */}
      <Box sx={{ bgcolor: '#f8faff', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                Still Need Help?
              </Typography>
              <Typography variant="body1" paragraph>
                Our support team is always ready to assist you with any questions or issues you may have.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  sx={{ borderRadius: 2, py: 1, px: 3 }}
                >
                  Contact Support
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary"
                  sx={{ borderRadius: 2, py: 1, px: 3 }}
                >
                  Join Community
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2488&q=80"
                  alt="Support Team"
                  sx={{ 
                    maxWidth: '100%', 
                    height: 'auto',
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default DocumentationPage; 